import React, { useCallback, useMemo, useEffect } from 'react';
import { useMedia } from 'react-use';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import qs from 'query-string';

import { GradeSelect } from 'common.components/grade-select/grade-select';
import { INFORMATICS_GRADES, JUNIOR_GRADES } from 'common.components/user/user.constants';
import { useChangeGrade } from 'common.components/grade-select/useChangeGrade';
import { getIsAuthenticated } from 'platform-components/src/components/user/selectors';
import { POPUP_AXIS, Select, SELECT_THEMES, Tabs, Text, useIntl } from 'platform-components';

const subjectsData = {
    informatics: INFORMATICS_GRADES,
    elementary_school: JUNIOR_GRADES,
} as const satisfies Record<string, number[]>;

export type SelectorSubjects = keyof typeof subjectsData;

const subjects = Object.keys(subjectsData) as SelectorSubjects[];

import './subject-grade-selector.scss';

interface SubjectGradeSelectorProps {
    onGradeSelect?: (grade: number) => void;
    onSubjectSelect?: (subject: SelectorSubjects) => void;
    defaultGrade?: number;
}

export const SubjectGradeSelector = ({ onSubjectSelect, defaultGrade }: SubjectGradeSelectorProps) => {
    const isMobile = useMedia('(max-width: 767px)');
    const { formatMessage } = useIntl();
    const { search } = useLocation();

    const isAuthedUser = useSelector(getIsAuthenticated);

    const i18n = (id: string) => formatMessage({ id: `subjectGradeSelector.titles.${id}` });
    const query = qs.parse(search);
    const selectedGrade = query.grade;

    const [activeGrades, activeSubject] = useMemo(() => {
        const selectedSubject =
            subjects.find((subject) => subjectsData[subject].includes(Number(selectedGrade))) || subjects[0];
        return [subjectsData[selectedSubject], selectedSubject];
    }, [selectedGrade]);

    useEffect(() => {
        if (activeSubject) {
            onSubjectSelect?.(activeSubject);
        }
    }, [activeSubject, onSubjectSelect]);

    const handleSelectGrade = useChangeGrade();
    const handleSelectSubject = useCallback(
        (subject: SelectorSubjects) => {
            const availableGrades = subjectsData[subject];

            if (!availableGrades) {
                return;
            }

            if (availableGrades.includes(Number(selectedGrade))) {
                return;
            }

            handleSelectGrade(availableGrades[0]);
            onSubjectSelect?.(subject);
        },
        [handleSelectGrade, selectedGrade, onSubjectSelect]
    );

    return (
        <div className="subject-grade-selector">
            {isMobile ? (
                <Select
                    mix="subject-grade-selector__subject-select"
                    onSelect={({ target }) => handleSelectSubject(target.value)}
                    popupAxis={POPUP_AXIS.END}
                    theme={SELECT_THEMES.ROUNDED}
                    value={activeSubject}
                    width="100%"
                >
                    {subjects.map((subject) => (
                        <Select.Option key={subject} value={subject}>
                            {i18n(subject)}
                        </Select.Option>
                    ))}
                </Select>
            ) : (
                <Tabs theme={Tabs.THEME.ROUNDED}>
                    {subjects.map((subject) => {
                        const isSelected = subject === activeSubject;

                        return (
                            <Tabs.Tab
                                id={subject}
                                key={subject}
                                onTabSelect={handleSelectSubject}
                                selected={isSelected}
                            >
                                <Text weight={isSelected ? Text.WEIGHT.M : Text.WEIGHT.S}>{i18n(subject)}</Text>
                            </Tabs.Tab>
                        );
                    })}
                </Tabs>
            )}
            <GradeSelect
                defaultGrade={defaultGrade}
                grades={activeGrades}
                mix="subject-grade-selector__grade-select"
                showEgeTab={activeSubject === 'informatics' && isAuthedUser}
                type={isMobile ? 'select' : 'tabs'}
            />
        </div>
    );
};
